/**
 * Prompt Test Bed page (PROC-75).
 *
 * Route: `/prompts/:key/evaluations`.
 *
 * The single edit surface for a prompt. Left column holds every
 * yaml-editable field under one form (`PromptDraftEditor` — role,
 * templated_instructions, instructions, technical_instructions, inputs,
 * sample_output, response_schema). Right column is the test-run panel
 * (`RunsPanel`) — pick a `LearnerTestProfile`, run the prompt against
 * the draft, see output + prior run history.
 *
 * All edits persist to ``draft_overrides`` on the ``Prompt`` row.
 * "Review draft →" in the footer navigates to
 * ``/prompts/:key/review-draft`` where the composed diff is shown and
 * the actual PR opens. PromptDetail is read-only.
 *
 * Test runs live in the ``PromptTestRun`` table; each row snapshots the
 * draft + profile at execution time so history stays honest as the
 * draft mutates.
 *
 * ``?suggestion=<id>`` opens the page on a prompt suggestion (PROC-266):
 * the editor edits the suggestion's draft, a run tests the live prompt
 * against it on the same input with the judge's call, History is its tests,
 * and Rio is the suggestion's own thread. Nothing touches the prompt's
 * ``draft_overrides``.
 */
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import remarkBreaks from "remark-breaks";

import { Button, Heading, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  cloneLearnerTestProfile,
  getPromptTestRun,
  listLearnerTestProfiles,
  listPromptTestRuns,
  startPromptTestRun,
  updateLearnerTestProfile,
} from "@api/rest/prompt-test-bed";
import type {
  LearnerTestProfile,
  LearnerTestProfileFile,
  MockLearnerData,
  PromptTestRun,
  PromptTestRunScopeReport,
} from "@api/rest/prompt-test-bed";
import {
  getGetPromptSuggestionQueryKey,
  getListPromptSuggestionTestsQueryKey,
  getListPromptSuggestionsQueryKey,
  getParsedTestFile,
  getPromptDraft as getPromptDraftRaw,
  startPromptSuggestionTest,
  useGetPromptDetails,
  useGetPromptSuggestion,
  useListAvailableInputs,
  useListAvailableModels,
  useListPromptSuggestionTests,
} from "@api/rest/types-and-hooks";
import type {
  GetPromptDraftOutput as PromptDraft,
  PromptRegistryInput,
  PromptSuggestion,
  PromptSuggestionTest,
  StartPromptSuggestionTestInput,
} from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { CopyButton } from "@components/CopyButton/CopyButton";
import { RunScorePanel } from "@components/EvalReview/RunScorePanel";
import { highlightChildren, highlightComponents } from "@components/EvalReview/highlightText";
import { modelLabel } from "@components/ModelTesting/constants";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { evalResultsPath, idParam } from "@pages/Evaluations/evalResultsProduction";
import {
  REDRAFT_POLL_MS,
  SUGGESTION_PARAM,
  SUGGESTION_STATUS_LABELS,
  TEST_VERDICT_LABELS,
  needsRedraft,
  redraftNote,
  suggestionDraft,
  testInFlight,
  testRunFailed,
  testedFieldLabels,
} from "@pages/Evaluations/promptSuggestionFields";
import { PromptDraftEditor } from "@pages/Prompts/PromptDraftEditor";
import { RioPromptChatDrawer } from "@pages/Prompts/RioPromptChatDrawer";
import { toggleMockKey } from "@pages/Prompts/mockInputToggle";
import { resolveCloneScopedKey } from "@pages/Prompts/profileCloneScope";

// The generated Orval fetchers return `{data, status, headers}`. We want
// the bare `PromptDraft` (matches the old manual-fetcher shape and keeps
// call sites simple). These helpers unwrap and throw on non-200.
async function unwrap<T>(resp: { data: T | { detail?: string }; status: number }): Promise<T> {
  if (resp.status !== 200) {
    const detail = (resp.data as { detail?: string })?.detail ?? `HTTP ${resp.status}`;
    throw new Error(detail);
  }
  return resp.data as T;
}
async function getPromptDraft(key: string): Promise<PromptDraft> {
  return unwrap(await getPromptDraftRaw(key));
}

// Query key namespaced by key so switching prompts doesn't share cache state.
// Kept in this file for the top-level page's `useQuery` — the merged
// `PromptDraftEditor` duplicates the tuple internally to avoid a
// cross-file import for a two-word constant.
function draftQueryKey(key: string) {
  return ["prompt-draft", key] as const;
}

// Query keys — kept as top-level fns so the runs panel and any parent
// invalidations reference the exact same tuple.
function profilesQueryKey(promptKey?: string) {
  // Prompt-namespaced so switching prompts refetches (the server now
  // returns universals + THIS prompt's scoped clones only) and so a
  // clone made under prompt X doesn't appear to leak into prompt Y's
  // cached list until it's manually invalidated.
  return promptKey ? (["learner-test-profiles", promptKey] as const) : (["learner-test-profiles"] as const);
}
function runsQueryKey(promptKey: string) {
  return ["prompt-test-runs", promptKey] as const;
}
function runDetailQueryKey(promptKey: string, runId: number) {
  return ["prompt-test-run", promptKey, runId] as const;
}

function ScopeReportWarning({ report }: { report: PromptTestRunScopeReport | undefined }) {
  if (!report) return null;
  const warnings: string[] = [];
  if (report.missing_input_keys?.length) {
    warnings.push(`Missing inputs: ${report.missing_input_keys.join(", ")}`);
  }
  if (report.dropped_mock_keys?.length) {
    warnings.push(`Dropped mock keys: ${report.dropped_mock_keys.join(", ")}`);
  }
  if (report.dropped_file_kinds?.length) {
    warnings.push(`Dropped file kinds: ${report.dropped_file_kinds.join(", ")}`);
  }
  if (!warnings.length) return null;
  return (
    <div className="flex flex-col gap-1 text-sm bg-amber-50 border border-amber-300 rounded-md p-3 text-amber-900">
      {warnings.map((w) => (
        <span key={w}>{w}</span>
      ))}
    </div>
  );
}

function RunStatusBadge({ status }: { status: PromptTestRun["status"] }) {
  const color =
    status === "complete"
      ? "bg-green-100 text-green-700"
      : status === "failed"
        ? "bg-red-100 text-red-600"
        : "bg-limestone-100 text-text-secondary";
  return <span className={`text-sm px-2 py-0.5 rounded ${color}`}>{status}</span>;
}

/** Read-only view of the snapshotted inputs a specific run sent to
 *  the workflow (which forwards them to the LLM activity). Collapsible
 *  so the run result stays the visual focus — the user opens this
 *  when they need to reason about "wait, did it actually see X?"
 *
 *  Snapshotted at run time by the ``PromptTestRun`` table, so the
 *  values here reflect what the run actually used even if the profile
 *  or draft has since been edited. */
function RunSnapshotView({ run }: { run: PromptTestRun }) {
  const [open, setOpen] = useState(false);
  const overrides = (run.draft_overrides_snapshot ?? {}) as Record<string, unknown>;
  const responseSchema = overrides.response_schema;
  const noInputMessage =
    run.status === "running"
      ? "Run is still in progress."
      : "run_llm was not invoked (required inputs were likely missing — see the scope report above).";
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
  return (
    <div className="flex flex-col gap-2 border-t border-border-subtle pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="cursor-pointer flex items-center gap-1.5 text-sm font-medium text-text-secondary hover:text-text-primary self-start"
      >
        <Chevron className="h-3.5 w-3.5 text-text-tertiary shrink-0" />
        <span>What was sent to the LLM</span>
      </button>
      {open && (
        <div className="flex flex-col gap-3 pl-5">
          {run.llm_input_snapshot == null ? (
            <div className="text-xs text-text-tertiary italic">{noInputMessage}</div>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Label size="small" colorType="secondary">
                  Payload
                </Label>
                <CopyButton value={run.llm_input_snapshot} ariaLabel="Copy payload to clipboard" />
              </div>
              <pre className="text-xs font-mono bg-bg-default rounded border border-border-subtle p-3 overflow-auto max-h-[50vh] whitespace-pre-wrap">
                {run.llm_input_snapshot}
              </pre>
            </div>
          )}
          {responseSchema != null && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Label size="small" colorType="secondary">
                  Response schema
                </Label>
                <CopyButton
                  value={typeof responseSchema === "string" ? responseSchema : JSON.stringify(responseSchema, null, 2)}
                  ariaLabel="Copy response schema to clipboard"
                />
              </div>
              <pre className="text-xs font-mono bg-bg-default rounded border border-border-subtle p-3 overflow-auto max-h-[30vh] whitespace-pre-wrap">
                {typeof responseSchema === "string" ? responseSchema : JSON.stringify(responseSchema, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RunResultView({
  promptKey,
  runId,
  onClose,
  scorable = true,
}: {
  promptKey: string;
  runId: number;
  onClose: () => void;
  /** False for a suggestion test's live run: scoring it scores the live
   *  prompt, which belongs on the prompt's own page. */
  scorable?: boolean;
}) {
  const queryClient = useQueryClient();
  // Strings an eval check objected to, marked up in the output below. Held
  // here rather than in the panel because this component renders the output.
  const [highlights, setHighlights] = useState<string[]>([]);
  // Live-polls one run — refetches every 2s while the workflow is
  // running. React Query's `refetchInterval` handles the polling; we
  // stop once the row transitions to complete/failed.
  const { data: run } = useQuery({
    queryKey: runDetailQueryKey(promptKey, runId),
    queryFn: () => getPromptTestRun(promptKey, runId),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "running" || s == null ? 2000 : false;
    },
  });
  // When this row transitions out of `running`, the polled history list
  // in the parent is stale — the sidebar badge for this run still says
  // "running." Invalidate the list query so the badge catches up.
  useEffect(() => {
    if (run && run.status !== "running") {
      queryClient.invalidateQueries({ queryKey: runsQueryKey(promptKey) });
    }
  }, [run?.status, run?.id, promptKey, queryClient, run]);
  if (!run) return null;
  // LLM outputs on schema-having prompts are usually a JSON object
  // whose values are markdown-formatted strings (one per section).
  // Displaying the raw JSON hides the markdown inside quoted values;
  // pretty-printing it exposes escape sequences (\n / **) instead of
  // rendered text. Detect that shape and split the object into
  // (key -> markdown) blocks so each value renders through ReactMarkdown.
  const markdownSections = (() => {
    if (!run.result) return null;
    const trimmed = run.result.trim();
    if (!trimmed.startsWith("{")) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return null;
      }
      const entries = Object.entries(parsed);
      if (entries.length === 0) return null;
      const stringOnly = entries.every(([, v]) => typeof v === "string");
      if (!stringOnly) return null;
      return entries as [string, string][];
    } catch {
      return null;
    }
  })();
  // Fallback pretty-JSON for parseable payloads that aren't the
  // markdown-sections shape (e.g. nested arrays / objects).
  const prettyJson = (() => {
    if (!run.result || markdownSections) return null;
    const trimmed = run.result.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return null;
    }
  })();
  // camelCase / snake_case -> Title Case for section headers.
  const humanizeKey = (k: string) =>
    k
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (c) => c.toUpperCase());
  return (
    <div className="flex flex-col gap-3">
      {/* Header row — summary line the user can scan without reading
          the body, plus a Close so a viewed run can be dismissed
          instead of squatting on the panel forever. */}
      <div className="flex items-center gap-2">
        <Label size="small" colorType="secondary">
          Run #{run.id}
        </Label>
        <RunStatusBadge status={run.status} />
        <div className="ml-auto flex items-center gap-1">
          {run.result && <CopyButton value={run.result} ariaLabel="Copy output to clipboard" />}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close run result"
            title="Close run result"
            className="rounded p-1 text-text-tertiary cursor-pointer hover:bg-bg-default hover:text-text-secondary"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      {run.scope_report && <ScopeReportWarning report={run.scope_report} />}
      {run.status === "failed" && (
        <Paragraph colorType="secondary" size="small" className="text-red-700">
          The workflow failed. Check pipelines logs (workflow id: {run.workflow_id}) for details.
        </Paragraph>
      )}
      {run.result && (
        <div className="max-h-[50vh] overflow-auto rounded-lg border border-border-subtle bg-bg-default p-4">
          {highlights.length > 0 && (
            <div className="mb-3 flex items-start justify-between gap-x-3 rounded border border-globemallow-300 bg-globemallow-50 px-2 py-1">
              {/* The quotes, not a count of them. A count claimed a passage
                  was marked even when none could be found — which happens
                  whenever a check quotes a JSON key, since keys render as
                  section headings rather than as text. Showing the quote says
                  what was objected to either way. */}
              <span className="text-xs text-globemallow-800">
                An eval check objected to:{" "}
                {highlights.slice(0, 3).map((quote, index) => (
                  <span key={`${quote}-${index}`}>
                    {index > 0 ? ", " : ""}
                    <span className="font-medium">
                      &ldquo;{quote.length > 60 ? `${quote.slice(0, 60)}…` : quote}&rdquo;
                    </span>
                  </span>
                ))}
                {highlights.length > 3 ? ` and ${highlights.length - 3} more` : ""}
                {". Marked below where it appears in the text."}
              </span>
              <button
                type="button"
                onClick={() => setHighlights([])}
                className="shrink-0 cursor-pointer text-xs text-globemallow-800 underline"
              >
                Clear
              </button>
            </div>
          )}
          {markdownSections ? (
            <div className="flex flex-col gap-4">
              {markdownSections.map(([key, content]) => (
                <div key={key} className="flex flex-col gap-1">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                    {humanizeKey(key)}
                  </h3>
                  <div className="prose prose-sm max-w-none text-text-primary [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                    {/* Highlights are applied to rendered children, not to
                        the markdown source — marking up the source would
                        corrupt it wherever a quote crosses a syntax
                        character. See highlightText. */}
                    <ReactMarkdown remarkPlugins={[remarkBreaks]} components={highlightComponents(highlights)}>
                      {content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          ) : prettyJson !== null ? (
            <pre className="text-xs font-mono leading-relaxed text-text-primary whitespace-pre-wrap break-words">
              {highlightChildren(prettyJson, highlights)}
            </pre>
          ) : (
            <div className="prose prose-sm max-w-none text-text-primary [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
              <ReactMarkdown remarkPlugins={[remarkBreaks]} components={highlightComponents(highlights)}>
                {run.result}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}
      {run.status !== "running" && <RunSnapshotView run={run} />}
      {scorable && run.status === "complete" && (
        <RunScorePanel runId={run.id} reviewedText={run.result} onHighlight={setHighlights} />
      )}
    </div>
  );
}

/** Modal that fetches the current parsed.json for a test file from
 *  S3 and lets the user override it for the run. The initial content
 *  is loaded from the backend on open; edits stay local to this
 *  session and get sent as `test_files_override[i].parsed_json`. The
 *  server's `_hydrate_test_file` treats an inline `parsed_json` as
 *  authoritative, so we don't have to persist anywhere. */
function ParsedJsonModal({
  file,
  initialOverride,
  onSave,
  onClose,
}: {
  file: LearnerTestProfileFile;
  initialOverride: string | undefined;
  onSave: (parsedJson: string) => void;
  onClose: () => void;
}) {
  // Throw on non-200 so React Query marks isError. Otherwise a 400/500
  // returns `{ status: 4xx, data: { detail } }` which useQuery treats
  // as success — the modal would then fall through to "No cached parse
  // output yet." even when the fetch itself failed, masking the real
  // error and blocking edit.
  const { data, isError } = useQuery({
    queryKey: ["parsed-test-file", file.s3_prefix],
    queryFn: async () => {
      const resp = await getParsedTestFile({ s3_prefix: file.s3_prefix });
      if (resp.status !== 200) {
        const detail = (resp.data as { detail?: string })?.detail ?? `HTTP ${resp.status}`;
        throw new Error(detail);
      }
      return resp.data;
    },
    enabled: initialOverride === undefined,
  });
  // Seed the buffer once — either from the user's saved override (if
  // they're re-opening the editor) or from S3. Later fetches don't
  // clobber in-progress edits.
  // buffer seeds from S3 once, then stays under the user's control.
  // Skipping the effect-copy and deriving would drop the user's edits
  // whenever the query re-renders; the set-state-in-effect disable
  // matches the pattern used elsewhere for one-shot server-to-form
  // seeding (see TemplateDetail).
  const [buffer, setBuffer] = useState<string | null>(initialOverride ?? null);
  useEffect(() => {
    if (buffer !== null) return;
    if (data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBuffer(data.parsed_json ?? "");
    }
  }, [buffer, data]);

  // The baseline is whatever the buffer was seeded from — an existing
  // override, or the S3 parsed_json (which may be null → empty seed).
  // We only fire `onSave` when the user actually diverged from it:
  // otherwise a click on "Use these values" after opening a file with
  // `parsed_json: null` would store an empty-string override that then
  // rides along on the next run and overrides the parse cache with "".
  const baseline: string | null = initialOverride !== undefined ? initialOverride : (data?.parsed_json ?? "");
  const isDirty = buffer !== null && buffer !== baseline;

  // Pretty-print JSON on save so the diff reads cleanly; fall back to
  // the raw buffer if it isn't valid JSON (the parse-status output is
  // sometimes plain text).
  const handleSave = () => {
    if (buffer === null) return;
    if (!isDirty) {
      // No change from what the server / prior override supplied →
      // don't create an override at all. Just dismiss.
      onClose();
      return;
    }
    let out = buffer;
    try {
      out = JSON.stringify(JSON.parse(buffer), null, 2);
    } catch {
      /* not JSON, keep as-is */
    }
    onSave(out);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-bg-surface rounded-xl p-6 max-w-3xl w-full flex flex-col gap-3 max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col min-w-0">
            <Heading size="small">Parsed values</Heading>
            <span className="text-xs text-text-tertiary truncate">
              {file.file_kind} · {file.filename}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-text-tertiary cursor-pointer hover:text-text-secondary"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {buffer === null ? (
          // buffer stays null until either (a) the query is still in
          // flight, (b) it errored, or (c) it succeeded and the seed
          // effect hasn't copied `data` into buffer yet — that last
          // window used to render "No cached parse output yet." which
          // was misleading. Treat everything short of a hard error as
          // "still loading"; once seeded, the textarea renders with
          // whatever content (including empty string for a null parse
          // cache) and the user can edit.
          <div className="text-xs text-text-tertiary p-3">{isError ? "Couldn't load parsed content." : "Loading…"}</div>
        ) : (
          <textarea
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            className="flex-1 min-h-[50vh] font-mono text-xs p-3 rounded border border-border-subtle bg-bg-default text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-900"
            spellCheck={false}
          />
        )}
        <div className="flex justify-end gap-2">
          <Button text="Cancel" appearance="secondary" onClick={onClose} />
          <button
            type="button"
            onClick={handleSave}
            disabled={buffer === null || !isDirty}
            className="text-sm px-3 py-2 rounded-md bg-rio-600 text-white cursor-pointer hover:bg-rio-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Use these values
          </button>
        </div>
      </div>
    </div>
  );
}

interface RunsPanelProps {
  promptKey: string;
  /** True while the editor has unsaved buffer content. Runs execute
   *  against the server-persisted draft, not the buffer, so we warn (and
   *  gate the Run button) when the two might diverge. */
  hasUnsavedDraftEdits: boolean;
  /** Set on a prompt suggestion's Review & test (PROC-266): a run tests the
   *  live prompt against the suggestion's saved draft on the same input, a
   *  judge compares them, and History is the suggestion's tests. */
  suggestion?: PromptSuggestion;
}

const SUGGESTION_TEST_POLL_MS = 4000;

const LIVE_AND_DRAFT_LABELS = { live: "Live prompt", draft: "Suggestion draft" } as const;

interface MockRow {
  key: string;
  /** What to render for this row. Drops the ``custom_fields.`` prefix
   *  so users see the bare field name they configured on the profile;
   *  ``key`` retains the prefix to keep row identity stable across the
   *  three state-tracking surfaces (see ``flattenMockRows`` comment).
   *  For non-custom rows label == key. */
  label: string;
  /** Truncated to 120 chars for read-only display next to the row. */
  preview: string;
  /** Untruncated string form of the value — used as the inline
   *  `<input>` seed and as the baseline for `isEdited`. Truncating
   *  either dropped content silently on save (short profile values
   *  were fine, long ones lost the tail). */
  rawValue: string;
  /** True for string / number / null values — rendered as an editable
   *  text input. Arrays and objects stay read-only (edit those in
   *  the profile editor). */
  editable: boolean;
}

/** Flatten `profile.mock_learner_data` into row-shaped entries. The
 *  `biographic_info` container is promoted to `biographic_info.<field>`
 *  rows; `custom_fields.<name>` becomes `custom_fields.<name>` rows;
 *  everything else keeps its top-level key. The `custom_fields.` prefix
 *  matters — a profile can legally have both `foo: X` at the top level
 *  AND `custom_fields.foo: Y`; without the prefix the two rows collide
 *  in `disabledMockKeys` / `mockValueOverrides` / React key lists. All
 *  three surfaces (display list, enable-checkbox key, override-build
 *  path) lean on the row key identifier staying stable. */
function flattenMockRows(md: MockLearnerData): MockRow[] {
  const rows: MockRow[] = [];
  const isEditablePrimitive = (x: unknown): boolean => x === null || typeof x === "string" || typeof x === "number";
  const pushRow = (key: string, label: string, v: unknown) => {
    const raw = String(v ?? "");
    rows.push({ key, label, preview: raw.slice(0, 120), rawValue: raw, editable: isEditablePrimitive(v) });
  };
  for (const [k, v] of Object.entries(md)) {
    if (v == null || (Array.isArray(v) && v.length === 0)) continue;
    if (k === "biographic_info" && typeof v === "object" && !Array.isArray(v)) {
      for (const [bk, bv] of Object.entries(v as Record<string, unknown>)) {
        if (bv == null || bv === "") continue;
        // Biographic keys are already unambiguous — the profile
        // editor doesn't let you name a top-level field the same as
        // a biographic sub-field. Prefix in both the key AND label
        // for clarity in the list.
        pushRow(`biographic_info.${bk}`, `biographic_info.${bk}`, bv);
      }
      continue;
    }
    if (k === "custom_fields" && typeof v === "object" && !Array.isArray(v)) {
      for (const [ck, cv] of Object.entries(v as Record<string, unknown>)) {
        // Row identity keeps the ``custom_fields.`` prefix (see
        // MockRow.label comment); display drops it so users see the
        // bare field name they configured.
        pushRow(`custom_fields.${ck}`, ck, cv);
      }
      continue;
    }
    pushRow(k, k, v);
  }
  return rows;
}

/** Seed the profile's mock baseline with empty placeholder entries for
 *  every key present in ``overrides`` but not in the baseline itself.
 *  Needed because ``buildMockOverride`` iterates the baseline — a user
 *  who types a value into a "missing from profile" row (a prompt input
 *  the profile doesn't have yet) would otherwise get their edit
 *  dropped. The empty seeds let ``buildMockOverride`` visit those keys
 *  and apply the override.
 *
 *  Key routing mirrors `flattenMockRows`:
 *   * ``biographic_info.<child>`` → nested under ``biographic_info``
 *   * ``custom_fields.<field>``   → nested under ``custom_fields``
 *   * anything else (no dot)      → top-level
 *  Dotted keys with an unfamiliar prefix (e.g. ``assessments.wasi``)
 *  pass through as top-level literal keys — same fate they'd have if
 *  authored on the profile page. */
function seedBaselineWithOverrideKeys(baseline: MockLearnerData, overrides: Record<string, string>): MockLearnerData {
  const out: Record<string, unknown> = JSON.parse(JSON.stringify(baseline ?? {}));
  for (const key of Object.keys(overrides)) {
    if (key.startsWith("biographic_info.")) {
      const sub = key.slice("biographic_info.".length);
      const bio = (out.biographic_info as Record<string, unknown> | undefined) ?? {};
      if (!(sub in bio)) bio[sub] = "";
      out.biographic_info = bio;
    } else if (key.startsWith("custom_fields.")) {
      const sub = key.slice("custom_fields.".length);
      const cf = (out.custom_fields as Record<string, unknown> | undefined) ?? {};
      if (!(sub in cf)) cf[sub] = "";
      out.custom_fields = cf;
    } else {
      // Anything else (plain top-level ``role``, arbitrary dotted paths
      // the prompt declares like ``assessments.wasi``) lands as a
      // literal top-level key. ``buildMockOverride``'s top-level branch
      // matches on the same string, so the override applies.
      if (!(key in out)) out[key] = "";
    }
  }
  return out as MockLearnerData;
}

/** Rebuild a MockLearnerData payload from the current enabled set and
 *  the per-row value overrides. Inverse of `flattenMockRows`. Rows in
 *  `valueOverrides` win over the profile's baseline value; disabled
 *  rows are dropped entirely. Enabled biographic children re-nest
 *  under `biographic_info`; enabled custom keys re-nest under
 *  `custom_fields`; empty containers get dropped so the payload
 *  matches "user unchecked everything" cleanly. */
function buildMockOverride(
  md: MockLearnerData,
  disabled: Set<string>,
  valueOverrides: Record<string, string>,
): MockLearnerData {
  const isEnabled = (key: string): boolean => !disabled.has(key);
  // "Save path" = the run mutation calls this once with disabled=∅ to
  // build the persistent mock; then again with disabled=<user's toggles>
  // for the per-run override. The container-drop logic only makes
  // sense in the second call — dropping empty containers on save
  // silently mutates the profile row. Detect the save path by an
  // empty disabled set and preserve containers unconditionally there.
  const savePath = disabled.size === 0;
  // Preserve every key from the profile by default. Keys that have a
  // UI row (i.e. were surfaced by `flattenMockRows`) get filtered by
  // `enabled` and can be value-overridden; keys the UI never showed
  // (empty arrays like `short_term_goals: []`, empty-string biographic
  // children, `null` provider_language) pass through untouched so a
  // single unrelated tweak doesn't silently omit them from the run.
  //
  // The predicates below MUST mirror the skip conditions in
  // `flattenMockRows` per branch — top-level skips null / empty array,
  // biographic children also skip empty strings.
  const topLevelHasUiRow = (val: unknown): boolean => !(val == null || (Array.isArray(val) && val.length === 0));
  const biographicHasUiRow = (val: unknown): boolean => !(val == null || val === "");
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(md)) {
    if (k === "biographic_info" && typeof v === "object" && !Array.isArray(v)) {
      const bio: Record<string, unknown> = {};
      // Track UI-visible children that stayed enabled. If none did,
      // the prompt-side badges `biographic_info` as MISSING (see
      // `anyBiographicEnabled`) — the payload must match by dropping
      // the whole container. Otherwise we'd emit
      // `biographic_info: {some_empty_field: ""}` (from the passthrough
      // branch) while claiming it's missing.
      let anyVisibleKept = false;
      for (const [bk, bv] of Object.entries(v as Record<string, unknown>)) {
        const rowKey = `biographic_info.${bk}`;
        const hasOverride = rowKey in valueOverrides;
        if (biographicHasUiRow(bv)) {
          if (!isEnabled(rowKey)) continue;
          bio[bk] = hasOverride ? valueOverrides[rowKey] : bv;
          anyVisibleKept = true;
        } else if (hasOverride) {
          // Baseline is empty (null / "") but the user typed a value
          // via the amber "required by prompt" row. Apply the
          // override and count the child as visible so the container
          // survives the anyVisibleKept gate below.
          bio[bk] = valueOverrides[rowKey];
          anyVisibleKept = true;
        } else {
          bio[bk] = bv;
        }
      }
      if (anyVisibleKept || savePath) out.biographic_info = bio;
      continue;
    }
    if (k === "custom_fields" && typeof v === "object" && !Array.isArray(v)) {
      const cf: Record<string, string> = {};
      for (const [ck, cv] of Object.entries(v as Record<string, unknown>)) {
        const rowKey = `custom_fields.${ck}`;
        // Custom fields always have a UI row — no null/empty skip.
        if (!isEnabled(rowKey)) continue;
        // `String(null)` yields the literal `"null"`. The UI seeds
        // null values as `""` (see `flattenMockRows`'s `pushRow`), so
        // match: coerce null/undefined to empty string here too, and
        // only stringify actual values.
        cf[ck] = rowKey in valueOverrides ? valueOverrides[rowKey] : cv == null ? "" : String(cv);
      }
      if (Object.keys(cf).length || savePath) out.custom_fields = cf;
      continue;
    }
    if (!topLevelHasUiRow(v)) {
      // No flatten row (null / empty-array default) — user couldn't
      // have DISABLED it via a checkbox, but they CAN still type a
      // value into the "required by prompt" amber row if the prompt
      // declares this key. Apply the override when present; otherwise
      // preserve the baseline. Without this the amber value was
      // silently dropped on save + run for defaults like
      // ``provider_language: null`` or ``safety_concerns: []``.
      out[k] = k in valueOverrides ? valueOverrides[k] : v;
      continue;
    }
    if (!isEnabled(k)) continue;
    // Top-level primitive → allow value override; otherwise pass the
    // raw profile value through so arrays/objects aren't stringified.
    if (k in valueOverrides) out[k] = valueOverrides[k];
    else out[k] = v;
  }
  return out as MockLearnerData;
}

interface ProfileVsPromptInputsProps {
  profile: LearnerTestProfile;
  promptKey: string;
  /** Row keys the user has explicitly turned OFF. Everything else is
   *  implicitly on — new rows that appear on refetch or before the
   *  parent's reset effect runs default to enabled. */
  disabledMockKeys: Set<string>;
  disabledFileUuids: Set<string>;
  /** Per-row value overrides keyed by the flattened row key (see
   *  `flattenMockRows`). Absent key = use the profile's baseline value. */
  mockValueOverrides: Record<string, string>;
  /** Per-run replacement for the prompt's declared input_keys. `null`
   *  Historical per-run override of the prompt's input list. Removed
   *  when the "Prompt expects" panel became read-only — the input list
   *  is now edited in ``PromptDraftEditor`` on the left column. */
  /** File uuid → edited parsed_json string. Rows with an entry here
   *  are flagged "edited"; the run sends `parsed_json` inline in the
   *  test_files_override list. */
  parsedJsonOverrides: Record<string, string>;
  onToggleMockKey: (key: string) => void;
  onToggleFile: (uuid: string) => void;
  onSetMockValue: (key: string, value: string) => void;
  onOpenParsedEditor: (uuid: string) => void;
  /** When a historical run is selected in the sidebar, its
   *  ``draft_overrides_snapshot`` gets passed here so the "Prompt
   *  expects" panel shows the inputs THAT RUN actually used —
   *  frozen at run time — instead of the live draft, which may have
   *  moved on since. Null while no historical run is selected (i.e.
   *  editing mode). */
  activeRunSnapshot?: { inputs?: { key: string }[] } | null;
  /** Same idea for the "Profile provides" side — snapshotted mock
   *  data at run time so history stays truthful even after the
   *  profile is edited. Null → fall back to the live profile.  */
  activeRunMock?: Record<string, unknown> | null;
  /** Snapshotted test-file references from the run. Same shape as
   *  ``profile.test_files`` (uuid + s3_prefix + kind + filename).
   *  Null → fall back to the live profile. */
  activeRunFiles?: LearnerTestProfileFile[] | null;
}

/** Side-by-side preview of what the profile provides vs. what the
 *  prompt's registry declares, with per-input checkboxes so a BCBA can
 *  test permutations (disable a mock key or a file, hit Run, see the
 *  effect without touching the underlying profile).
 *
 *  Unchecked entries get sent as `mock_learner_data_override` /
 *  `test_files_override` on the run — the run's snapshot then reflects
 *  the trimmed inputs, so History tells the honest story of what was
 *  actually passed. */
function ProfileVsPromptInputs({
  profile,
  promptKey,
  disabledMockKeys,
  disabledFileUuids,
  mockValueOverrides,
  parsedJsonOverrides,
  onToggleMockKey,
  onToggleFile,
  onSetMockValue,
  onOpenParsedEditor,
  activeRunSnapshot,
  activeRunMock,
  activeRunFiles,
}: ProfileVsPromptInputsProps) {
  const { data: detailsResp } = useGetPromptDetails(promptKey);
  const { data: draft } = useQuery({
    queryKey: draftQueryKey(promptKey),
    queryFn: () => getPromptDraft(promptKey),
  });
  // Read-only view of what the prompt declares as its inputs. Edits
  // happen on the left panel's ``PromptDraftEditor`` and land in
  // ``draft_overrides.inputs``. Prefer the draft's list so this panel
  // reflects the user's in-flight intent; falls back to registry
  // (committed yaml). The runner also honors draft_overrides.inputs
  // when ``use_draft=true``, so what's shown here matches what the
  // run will scope against.
  const registryInputs: PromptRegistryInput[] = detailsResp?.status === 200 ? (detailsResp.data.inputs ?? []) : [];
  const registryByKey = new Map(registryInputs.map((i) => [i.key, i] as const));
  // Wider input catalog — every input the registry knows about, not
  // just what the yaml source of THIS prompt declares. Used to
  // resolve metadata (source, file_kind, …) for inputs a draft added
  // that the yaml source hasn't caught up to. Without this, adding an
  // input like ``assessments.fast`` via the draft renders as
  // "(custom) — missing" even when the profile does have a matching
  // FAST-kind file.
  const { data: allInputsResp } = useListAvailableInputs();
  const allInputsByKey = useMemo(() => {
    if (allInputsResp?.status !== 200) return new Map<string, PromptRegistryInput>();
    return new Map((allInputsResp.data.inputs as PromptRegistryInput[]).map((i) => [i.key, i]));
  }, [allInputsResp]);
  // Resolve a possibly-dotted input key (e.g. ``interviews.parent``)
  // to the specific file_kind that satisfies it. The pipelines catalog
  // emits ``nested_key_file_kinds`` on parent inputs like ``interviews``
  // — a map of subkey → file kind (parent → INTERVIEW_PARENT etc.).
  // Without this resolution, a prompt declaring ``interviews.parent``
  // would render as missing even after the user uploaded a
  // ``INTERVIEW_PARENT`` file, because the flat catalog lookup on the
  // dotted key returns nothing.
  //
  // The ``useListAvailableInputs`` payload is loosely typed on the
  // frontera side (JSONField) so ``nested_key_file_kinds`` flows
  // through untouched; the strict ``PromptInputSerializer`` on
  // ``useGetPromptDetails`` would drop it, which is why we resolve
  // against ``allInputsByKey``.
  const resolveNestedFileKind = (key: string): string | undefined => {
    if (!key.includes(".")) return undefined;
    const dot = key.indexOf(".");
    const parent = key.slice(0, dot);
    const sub = key.slice(dot + 1);
    const parentEntry = allInputsByKey.get(parent) as
      | (PromptRegistryInput & { nested_key_file_kinds?: Record<string, string> })
      | undefined;
    return parentEntry?.nested_key_file_kinds?.[sub];
  };
  // When a historical run is selected, its snapshotted overrides win
  // over the live draft — that's the whole point of the snapshot.
  const draftOverrides: { inputs?: { key: string }[] } | null | undefined = activeRunSnapshot
    ? activeRunSnapshot
    : (draft?.draft_overrides as { inputs?: { key: string }[] } | null | undefined);
  // PROC-106: build promptInputs from the RAW dotted input_keys (draft
  // if present, else the yaml source's ``input_keys`` field), not from
  // ``detailsResp.data.inputs`` which api-django collapses down to
  // parent entries. Losing the dotted specificity breaks per-role
  // file-kind matching for entries like ``interviews.other`` — the
  // parent entry's ``file_kind`` is ``INTERVIEW``, so an
  // ``INTERVIEW_OTHER`` file gets dimmed as "not used by prompt"
  // even though it exactly satisfies the dotted input the prompt
  // declares. Threading the raw keys through the same synthesis logic
  // used for draft-only inputs recovers the specificity.
  const rawInputKeys: string[] = draftOverrides?.inputs
    ? Array.from(new Set(draftOverrides.inputs.map((slot) => slot.key)))
    : detailsResp?.status === 200
      ? (detailsResp.data.input_keys ?? [])
      : [];
  const promptInputs: PromptRegistryInput[] = rawInputKeys.map((key) => {
    // For a dotted key on a parent that carries ``nested_key_file_kinds``
    // (currently only ``interviews``), the specific role's file kind
    // is the one that actually satisfies this input — override any
    // parent-derived file_kind with it.
    const nestedFileKind = resolveNestedFileKind(key);
    const yamlHit = registryByKey.get(key);
    if (yamlHit) return nestedFileKind ? { ...yamlHit, file_kind: nestedFileKind } : yamlHit;
    const wideHit = allInputsByKey.get(key);
    if (wideHit) return nestedFileKind ? { ...wideHit, file_kind: nestedFileKind } : wideHit;
    return { key, source: "custom", file_kind: nestedFileKind };
  });

  // Historical-run mode: use the run's snapshotted mock + file
  // references so the "Profile provides" panel matches what the run
  // actually sent to the LLM. Falls back to the live profile in
  // editing mode.
  const md = activeRunMock ?? profile.mock_learner_data ?? {};
  const mockRows = flattenMockRows(md);
  const displayFiles: LearnerTestProfileFile[] = activeRunFiles ?? profile.test_files;

  const isMockEnabled = (key: string): boolean => !disabledMockKeys.has(key);
  const isFileEnabled = (uuid: string): boolean => !disabledFileUuids.has(uuid);

  // Sets used for the pre-flight "will this row actually reach the LLM?"
  // signal. A row that's checked can STILL be scoped out by the backend
  // if the prompt's declared inputs don't ask for it — dimming those
  // rows here lets a user glance-and-see what's really going to be
  // sent, without the noisy "not used" text badges we tried before.
  //
  // Empty prompt inputs (registry-less prompt) → treat every row as
  // "used" so we don't dim everything into oblivion.
  const promptKeys = new Set(promptInputs.map((i) => i.key));
  const promptFileKinds = new Set(promptInputs.filter((i) => i.file_kind).map((i) => i.file_kind as string));
  const isRowUsedByPrompt = (rowKey: string): boolean => {
    if (promptKeys.size === 0) return true;
    if (promptKeys.has(rowKey)) return true;
    if (rowKey.startsWith("custom_fields.")) {
      // Backend hoists ``custom_fields.<foo>`` → top-level ``<foo>``,
      // so a bare-key prompt input satisfies it; also match the
      // container-level ``custom_fields`` key some prompts declare.
      const bare = rowKey.slice("custom_fields.".length);
      return promptKeys.has(bare) || promptKeys.has("custom_fields");
    }
    if (rowKey.startsWith("biographic_info.")) {
      // Prompts almost always declare the container ``biographic_info``,
      // not per-child keys — one match covers every child.
      return promptKeys.has("biographic_info") || promptKeys.has(rowKey);
    }
    return false;
  };
  const isFileUsedByPrompt = (kind: string): boolean => (promptFileKinds.size === 0 ? true : promptFileKinds.has(kind));

  // Mock-typed prompt inputs the profile doesn't already have. Rendered
  // as empty editable rows below the existing ones so the user can fill
  // them in inline instead of navigating to the profile editor.
  //   * file_kind !== undefined → handled in the Files section, skip here
  //   * key already present on the profile (directly, via custom_fields
  //     hoisting, or via a container↔child relationship) → skip
  //
  // Container↔child relationship: a prompt declaring the container
  // ``biographic_info`` (or ``custom_fields``) is satisfied by ANY
  // child row of the same container — the profile UI splits the
  // container into ``biographic_info.name`` / ``.age`` / ``.gender``
  // rows and never carries the bare container key. Without this rule,
  // every profile with a nested biographic block redundantly rendered
  // a "biographic_info — required by prompt" empty row alongside its
  // children. Same logic in reverse for the ``notUsedByPrompt`` badge
  // below on child rows.
  const existingProfileKeyLookup = new Set(
    mockRows.flatMap((r) => {
      if (r.key.startsWith("custom_fields.")) {
        return [r.key, r.key.slice("custom_fields.".length), "custom_fields"];
      }
      if (r.key.startsWith("biographic_info.")) {
        return [r.key, "biographic_info"];
      }
      return [r.key];
    }),
  );
  const missingMockInputs = promptInputs.filter((i) => !i.file_kind && !existingProfileKeyLookup.has(i.key));
  // File-typed prompt inputs whose ``file_kind`` isn't represented in
  // the profile's current files. Surfaced pre-flight so a required
  // upload isn't invisible before Run — the amber "required by prompt"
  // row directs the user to the profile editor to upload one (files
  // can't be added inline from the test bed).
  //
  // A file input can also be satisfied by MOCK data injection: a
  // ``custom_fields.CLINICAL_NOTES`` entry on the profile gets hoisted
  // to top-level ``CLINICAL_NOTES`` server-side and feeds the same
  // slot the file's parsed content would have. Mirror the previous
  // Prompt-expects logic here so profiles using the mock-injection
  // path don't get a false-amber "required by prompt" file row.
  const existingFileKinds = new Set(displayFiles.map((f) => f.file_kind));
  const missingFileInputs = promptInputs.filter((i) => {
    if (!i.file_kind) return false;
    if (existingFileKinds.has(i.file_kind)) return false;
    // Mock-injection fallbacks: profile satisfies the file_kind via a
    // top-level mock key (either the ``file_kind`` uppercase form or
    // the ``input.key`` lowercase form; ``custom_fields.<X>`` counts
    // because the backend hoists to top-level ``<X>``).
    return !existingProfileKeyLookup.has(i.file_kind) && !existingProfileKeyLookup.has(i.key);
  });

  return (
    <div className="mt-2 flex flex-col gap-3 border-t border-border-subtle pt-3">
      {/* Profile's own description sits above every subsection — it
          applies to the profile as a whole, not just the inputs list. */}
      {profile.notes && <div className="text-xs text-text-tertiary italic">{profile.notes}</div>}
      <div className="flex flex-col gap-1.5">
        <Label size="small" colorType="secondary" className="uppercase tracking-wide">
          Inputs
        </Label>
        {mockRows.length === 0 && missingMockInputs.length === 0 && displayFiles.length === 0 ? (
          <span className="text-sm text-text-tertiary italic">No mock data or files.</span>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {mockRows.map((r) => {
              const enabled = isMockEnabled(r.key);
              const usedByPrompt = isRowUsedByPrompt(r.key);
              // Dim when either: user unchecked it, OR the prompt won't
              // read it (would be scoped out backend-side). Same dimmed
              // treatment for both — the user cares about "will this
              // reach the LLM", and either state produces "no".
              const cls = !enabled || !usedByPrompt ? "text-text-tertiary opacity-60" : "text-text-secondary";
              // Use `rawValue` (untruncated) as the baseline so long
              // profile strings don't get silently clipped to 120 chars
              // when the user's edit is saved back.
              const currentValue = r.key in mockValueOverrides ? mockValueOverrides[r.key] : r.rawValue;
              const isEdited = r.key in mockValueOverrides && mockValueOverrides[r.key] !== r.rawValue;
              return (
                <li key={r.key} className={cls}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => onToggleMockKey(r.key)}
                      className="shrink-0 cursor-pointer"
                      aria-label={`Include ${r.label}`}
                    />
                    <span className="font-medium text-text-primary shrink-0">{r.label}</span>
                    <span className="text-text-tertiary shrink-0">:</span>
                    {r.editable ? (
                      <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => onSetMockValue(r.key, e.target.value)}
                        disabled={!enabled}
                        className="flex-1 min-w-0 text-xs rounded border border-border-subtle bg-bg-surface px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-rio-400 disabled:opacity-60"
                      />
                    ) : (
                      <span className="text-text-tertiary flex-1 min-w-0 truncate">{r.preview}</span>
                    )}
                    {isEdited && (
                      <span className="text-xs text-amber-600 shrink-0" title="Will save to the profile on next Run">
                        edited
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
            {/* Prompt-declared mock inputs missing from the profile.
                Empty editable — typing here + clicking Run adds the
                value to the profile via PATCH before starting the run.
                Checkbox toggles ``disabledMockKeys`` like normal rows
                so the user can skip a specific run without clearing
                the typed value. */}
            {missingMockInputs.map((input) => {
              const enabled = isMockEnabled(input.key);
              const currentValue = mockValueOverrides[input.key] ?? "";
              const isEdited = input.key in mockValueOverrides && mockValueOverrides[input.key] !== "";
              return (
                <li
                  key={`missing:${input.key}`}
                  className={enabled ? "text-text-secondary" : "text-text-tertiary opacity-60"}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => onToggleMockKey(input.key)}
                      className="shrink-0 cursor-pointer"
                      aria-label={`Include ${input.key}`}
                    />
                    <span className="font-medium text-text-primary shrink-0">
                      {input.key}
                      <span className="text-red-600 ml-0.5" title="Required by prompt" aria-label="required">
                        *
                      </span>
                    </span>
                    <span className="text-text-tertiary shrink-0">:</span>
                    <input
                      type="text"
                      value={currentValue}
                      disabled={!enabled}
                      onChange={(e) => onSetMockValue(input.key, e.target.value)}
                      placeholder="(empty — fill in)"
                      className="flex-1 min-w-0 text-xs rounded border border-amber-300 bg-amber-50/40 px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-rio-400 disabled:opacity-60"
                    />
                    {isEdited && (
                      <span className="text-xs text-amber-600 shrink-0" title="Will save to the profile on next Run">
                        edited
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {(displayFiles.length > 0 || missingFileInputs.length > 0) && (
        <div className="flex flex-col gap-1.5">
          <Label size="small" colorType="secondary" className="uppercase tracking-wide">
            Files
          </Label>
          <ul className="flex flex-col gap-1 text-sm">
            {displayFiles.map((f) => {
              const enabled = isFileEnabled(f.file_uuid);
              const usedByPrompt = isFileUsedByPrompt(f.file_kind);
              const parsedEdited = f.file_uuid in parsedJsonOverrides;
              const cls = !enabled || !usedByPrompt ? "text-text-tertiary opacity-60" : "text-text-secondary";
              return (
                <li key={f.file_uuid} className={cls}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => onToggleFile(f.file_uuid)}
                      className="shrink-0 cursor-pointer"
                      aria-label={`Include ${f.filename}`}
                    />
                    <span className="font-medium text-text-primary shrink-0">{f.file_kind}</span>
                    <span className="text-text-tertiary flex-1 min-w-0 truncate">: {f.filename}</span>
                    {parsedEdited && (
                      <span className="text-xs text-amber-600 shrink-0" title="Parsed values overridden for this run">
                        edited
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => onOpenParsedEditor(f.file_uuid)}
                      aria-label={
                        parsedEdited ? "Edit parsed values for this file" : "View parsed values for this file"
                      }
                      title={parsedEdited ? "Edit parsed values" : "View parsed values"}
                      className={`shrink-0 rounded p-1 cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                        parsedEdited
                          ? "text-amber-600 hover:bg-amber-50"
                          : "text-text-tertiary hover:bg-bg-default hover:text-text-secondary"
                      }`}
                      disabled={!enabled}
                    >
                      {parsedEdited ? <PencilIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </li>
              );
            })}
            {/* Prompt-declared file inputs the profile lacks. Rendered
                UNCHECKED — the checkbox convention on this page is
                "will this reach the LLM?" and there's no file to send.
                Disabled because there's no inline upload path from the
                test bed; the user has to add the file in the profile
                editor. */}
            {missingFileInputs.map((input) => (
              <li key={`missing-file:${input.file_kind}`} className="text-text-tertiary opacity-60">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={false}
                    disabled
                    className="shrink-0"
                    aria-label={`${input.file_kind} not uploaded`}
                  />
                  <span className="font-medium shrink-0">
                    {input.file_kind}
                    <span className="text-red-600 ml-0.5" title="Required by prompt" aria-label="required">
                      *
                    </span>
                  </span>
                  <span className="flex-1 min-w-0 truncate italic">
                    : no file uploaded — add one from the profile editor
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Clone-primary dialog. Cloning a profile into a per-prompt variant
 *  is the 90% flow ("test N scenario variants of one input"), so it
 *  gets the whole hero section. Rename and publish-to-global are
 *  secondary maintenance — tucked into a "More actions" disclosure so
 *  they don't compete with the primary name input. */
function ProfileActionsDialog({
  profile,
  onClose,
  onRename,
  isRenaming,
  onCloneAsVariant,
  isCloning,
  onPublishAsGlobal,
  isPublishing,
}: {
  profile: LearnerTestProfile;
  onClose: () => void;
  onRename: (name: string) => void;
  isRenaming: boolean;
  onCloneAsVariant: (name: string, global: boolean) => void;
  isCloning: boolean;
  onPublishAsGlobal: () => void;
  isPublishing: boolean;
}) {
  const defaultCloneName = `${profile.name} (copy)`;
  const [cloneName, setCloneName] = useState(defaultCloneName);
  const [cloneAsGlobal, setCloneAsGlobal] = useState(false);
  const [renameValue, setRenameValue] = useState(profile.name);
  const isVariant = Boolean(profile.scoped_prompt_key);
  const renameDirty = renameValue.trim().length > 0 && renameValue.trim() !== profile.name;
  const canClone = cloneName.trim().length > 0 && !isCloning;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      tabIndex={-1}
    >
      <div
        className="bg-bg-surface rounded-xl shadow-xl w-[min(560px,95vw)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — anchors the dialog on the profile itself. The name
            is the subject; every section below acts on it. */}
        <div className="relative px-6 pt-6 pb-4 border-b border-border-subtle">
          <Heading size="small">Profile settings</Heading>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close profile settings"
            className="absolute right-4 top-4 rounded p-1 text-text-tertiary hover:bg-bg-default hover:text-text-primary"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <div className="mt-2 inline-flex items-center rounded border border-rio-200 bg-rio-50 px-2 py-0.5 text-xs font-mono text-rio-800">
            {profile.name}
          </div>
        </div>

        {/* Three parallel action sections. Each shows a label, a short
            explanation, an input (when there is one), and its own
            primary button on the right. Reads left-to-right like a
            settings row, not a nested subform. */}
        <div className="px-6 py-5 flex flex-col divide-y divide-border-subtle">
          {/* Clone — scoped to this prompt by default; the checkbox
              below sends scoped_prompt_key: null instead so the copy
              lands global in one step, without a second trip through
              "Share with all prompts" on the resulting copy (PROC-190). */}
          <div className="pb-5 flex flex-col gap-2">
            <Label size="small" colorType="primary">
              Make a copy
            </Label>
            <Paragraph size="small" colorType="secondary">
              The original profile stays untouched.
            </Paragraph>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                value={cloneName}
                autoFocus
                onFocus={(e) => e.currentTarget.select()}
                onChange={(e) => setCloneName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canClone) onCloneAsVariant(cloneName.trim(), cloneAsGlobal);
                }}
                className="flex-1 border border-border-medium rounded-md px-3 py-1.5 bg-bg-default text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-rio-400"
              />
              <Button
                appearance="primary"
                text={isCloning ? "Copying…" : "Make copy"}
                disabled={!canClone}
                onClick={() => onCloneAsVariant(cloneName.trim(), cloneAsGlobal)}
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={cloneAsGlobal}
                onChange={(e) => setCloneAsGlobal(e.target.checked)}
                className="shrink-0"
              />
              Share with all prompts instead of just this one
            </label>
          </div>

          {/* Rename */}
          <div className="py-5 flex flex-col gap-2">
            <Label size="small" colorType="primary">
              Rename
            </Label>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && renameDirty && !isRenaming) onRename(renameValue.trim());
                }}
                className="flex-1 border border-border-medium rounded-md px-3 py-1.5 bg-bg-default text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-rio-400"
              />
              <Button
                appearance="secondary"
                text={isRenaming ? "Saving…" : "Save"}
                disabled={!renameDirty || isRenaming}
                onClick={() => onRename(renameValue.trim())}
              />
            </div>
          </div>

          {/* Publish as global — only meaningful for a variant. A
              global profile has nothing to publish. */}
          {isVariant && (
            <div className="pt-5 flex flex-col gap-2">
              <Label size="small" colorType="primary">
                Share with all prompts
              </Label>
              <Paragraph size="small" colorType="secondary">
                Makes this copy available on every prompt.
              </Paragraph>
              <div className="flex justify-end">
                <Button
                  appearance="secondary"
                  text={isPublishing ? "Sharing…" : "Share with all prompts"}
                  disabled={isPublishing}
                  onClick={onPublishAsGlobal}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RunsPanel({ promptKey, hasUnsavedDraftEdits, suggestion }: RunsPanelProps) {
  const queryClient = useQueryClient();
  // The suggestion test on show: its two runs, with the judge's call from
  // the polled list once that has it.
  const [activeTest, setActiveTest] = useState<PromptSuggestionTest | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  // Run against a candidate model without leaving the prompt editor —
  // the follow-through after a multi-model test picks a winner: edit the
  // draft here and flip between current and candidate runs. "" = the
  // pipeline default (current model).
  const [runModel, setRunModel] = useState<string>("");
  const { data: modelsResponse } = useListAvailableModels();
  const availableModels = modelsResponse?.status === 200 ? modelsResponse.data : null;
  // The most-recently-started run — auto-shows its result below the picker
  // so the BCBA doesn't have to hunt through history for the run they just
  // triggered.
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  // Only an explicit History click flips the inputs panel into read-only
  // snapshot mode — a just-finished run shows its RESULT but leaves the
  // inputs live, so the next run can be tweaked without closing anything.
  const [inspectRunInputs, setInspectRunInputs] = useState(false);
  // Fetch the ACTIVE run's detail (including its snapshotted draft
  // overrides + mock/files) so the Profile vs Prompt panel can show
  // what THAT RUN actually used — not the live draft, which mutates
  // as the user edits after the run finished. Without this, opening
  // a historical run in the sidebar renders the live state and the
  // history looks like it's "changing" every time the draft is edited.
  const { data: activeRunDetail } = useQuery({
    queryKey: activeRunId != null ? runDetailQueryKey(promptKey, activeRunId) : ["prompt-test-run", "none"],
    queryFn: () => getPromptTestRun(promptKey, activeRunId!),
    enabled: activeRunId != null,
  });
  // Per-profile "disabled" deltas for the input-permutation checkboxes.
  // Storing what the user has TURNED OFF (rather than what's currently
  // on) means:
  //   * default = everything on, no seed effect needed → Run works
  //     immediately after picking a profile
  //   * new rows / files that appear on refetch are on by default (not
  //     silently unchecked because the enabled snapshot was stale)
  //   * the "all on" reset when switching profiles is just an empty
  //     set — no re-derivation from `mockRows`
  const [disabledMockKeys, setDisabledMockKeys] = useState<Set<string>>(new Set());
  const [disabledFileUuids, setDisabledFileUuids] = useState<Set<string>>(new Set());
  // Per-row string overrides for editable mock values. Row key → new
  // value. Absent = use the profile's baseline. Only rows whose value
  // is a primitive (biographic children, custom fields, top-level
  // strings) get exposed for editing.
  const [mockValueOverrides, setMockValueOverrides] = useState<Record<string, string>>({});
  // Per-file parsed_json overrides keyed by file_uuid. Absent = use
  // whatever S3 has for the profile's file. Populated when the user
  // opens the "View parsed" modal and saves edits. Attached to
  // `test_files_override` entries on the run so `_hydrate_test_file`
  // takes the inline payload instead of re-reading S3.
  const [parsedJsonOverrides, setParsedJsonOverrides] = useState<Record<string, string>>({});
  // Which file is currently open in the parsed-json modal, if any.
  const [parsedModalFileUuid, setParsedModalFileUuid] = useState<string | null>(null);
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false);

  const { data: profiles = [] } = useQuery({
    queryKey: profilesQueryKey(promptKey),
    // Test bed doesn't filter by report_type — a prompt can belong to
    // many report-type flows, so we show every profile the caller can
    // see (globals plus their org, once we wire org context through).
    // ``prompt_key`` filters server-side: universals + this prompt's
    // scoped clones. Other prompts' clones stay hidden.
    queryFn: () => listLearnerTestProfiles({ prompt_key: promptKey }),
  });
  const { data: runs = [] } = useQuery({
    queryKey: runsQueryKey(promptKey),
    queryFn: () => listPromptTestRuns(promptKey),
    enabled: !suggestion,
  });
  const { data: testsResp } = useListPromptSuggestionTests(suggestion?.id ?? 0, {
    query: {
      enabled: !!suggestion,
      refetchInterval: (query) => {
        const resp = query.state.data;
        return resp?.status === 200 && resp.data.tests.some((t) => testInFlight(t)) ? SUGGESTION_TEST_POLL_MS : false;
      },
    },
  });
  const tests = testsResp?.status === 200 ? testsResp.data.tests : [];
  const shownTest = activeTest ? (tests.find((t) => t.id === activeTest.id) ?? activeTest) : null;

  const selectedProfile: LearnerTestProfile | undefined = profiles.find((p) => p.id === selectedProfileId);

  // Wipe every per-run override when the picked profile changes.
  // Because the default disabled-set is empty (= "all on"), there is
  // NO seed effect racing the first checkbox render or a same-tick
  // Run click — that's the whole point of storing disable deltas
  // rather than the enabled snapshot. This effect just handles the
  // profile-change reset. New rows appearing after a background
  // refetch of the same profile also default to on for free (they're
  // simply absent from the disabled set).
  const lastSeenProfileId = useRef<number | null>(null);
  useEffect(() => {
    const id = selectedProfile?.id ?? null;
    if (lastSeenProfileId.current === id) return;
    lastSeenProfileId.current = id;
    setDisabledMockKeys(new Set());
    setDisabledFileUuids(new Set());
    setMockValueOverrides({});
    setParsedJsonOverrides({});
    setParsedModalFileUuid(null);
  }, [selectedProfile?.id]);

  // If the file the modal is pointing at disappears (profile refetched,
  // file was deleted upstream, etc.) the modal render returns null. We
  // also need to clear the pointer state so a later reopen actually
  // remounts the component — otherwise setting the same uuid again is a
  // no-op and the UI looks stuck. Cross-system sync case (React state
  // mirroring external data), which the general "no setState in
  // effect" rule doesn't accommodate.
  useEffect(() => {
    if (parsedModalFileUuid === null) return;
    const stillHere = selectedProfile?.test_files.some((f) => f.file_uuid === parsedModalFileUuid) ?? false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!stillHere) setParsedModalFileUuid(null);
  }, [parsedModalFileUuid, selectedProfile]);

  // Save-and-run: value edits fold into the profile row (PATCH) BEFORE
  // the run fires. Once that settles the run is started against the
  // now-current profile; per-run tweaks (disabled keys/files, parsed
  // edits, "Extras this run only") layer on top as overrides.
  //
  // Motivation (user feedback): the previous flow made users bounce to
  // the profile editor page to persist a value tweak, then come back to
  // click Run. Merging save + run collapses that into one click on the
  // test bed itself.
  const startRun = useMutation({
    mutationFn: async () => {
      type FileOverride = LearnerTestProfileFile & { parsed_json?: string };
      const input: {
        learner_test_profile_id: number | null;
        use_draft?: boolean;
        model?: string;
        mock_learner_data_override?: MockLearnerData;
        test_files_override?: FileOverride[];
      } = {
        learner_test_profile_id: selectedProfileId,
        // A suggestion's test runs both the live prompt and its draft.
        ...(suggestion ? {} : { use_draft: true }),
        // Omitted = pipeline default; set = candidate-model run.
        ...(runModel ? { model: runModel } : {}),
      };

      // Persist value edits (only value edits — disabled-checkbox and
      // file-toggle stay per-run). Requires a selected profile.
      let effectiveMock: MockLearnerData = selectedProfile?.mock_learner_data ?? {};
      if (selectedProfile) {
        // Seed the baseline with empty placeholders for any override key
        // the profile doesn't have yet (i.e. a prompt-declared input the
        // user just filled in). Otherwise buildMockOverride wouldn't
        // visit it — it iterates the baseline.
        const seeded = seedBaselineWithOverrideKeys(selectedProfile.mock_learner_data ?? {}, mockValueOverrides);
        // Persistent shape: value overrides applied, nothing disabled.
        // (disabled-checkbox is a per-run tweak, not a delete.)
        const persistentMock = buildMockOverride(seeded, new Set(), mockValueOverrides);
        if (JSON.stringify(persistentMock) !== JSON.stringify(selectedProfile.mock_learner_data ?? {})) {
          // Prefix the save-side error so the UI can distinguish it
          // from a run-side failure — the message surfaces via
          // ``startRun.error`` in the panel below the Run button.
          try {
            await updateLearnerTestProfile(selectedProfile.id, { mock_learner_data: persistentMock });
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            throw new Error(`Save failed before run: ${msg}`);
          }
          // Invalidate the profile caches HERE — as soon as the PATCH
          // lands — rather than waiting for the whole mutation's
          // onSuccess. Otherwise a startPromptTestRun failure below
          // would leave the DB updated but the React Query cache stale,
          // and a subsequent unchanged run would hydrate from the DB
          // while the UI still showed pre-save mock values.
          queryClient.invalidateQueries({ queryKey: profilesQueryKey(promptKey) });
          queryClient.invalidateQueries({ queryKey: ["learner-test-profile"] });
        }
        effectiveMock = persistentMock;
      }

      // Per-run mock override: only when the user disabled a mock key.
      // Built off the POST-PATCH baseline so any value edits are already
      // in there.
      if (disabledMockKeys.size > 0) {
        const seededPost = seedBaselineWithOverrideKeys(effectiveMock, mockValueOverrides);
        input.mock_learner_data_override = buildMockOverride(seededPost, disabledMockKeys, mockValueOverrides);
      }

      // Per-run file tweaks unchanged: toggles/parsed-edits still layer
      // as an override without touching the profile row.
      if (selectedProfile) {
        const filesChanged = disabledFileUuids.size > 0;
        const anyParsedEdited = selectedProfile.test_files.some((f) => f.file_uuid in parsedJsonOverrides);
        if (filesChanged || anyParsedEdited) {
          input.test_files_override = selectedProfile.test_files
            .filter((f) => !disabledFileUuids.has(f.file_uuid))
            .map((f) => {
              const override = parsedJsonOverrides[f.file_uuid];
              return override !== undefined ? { ...f, parsed_json: override } : { ...f };
            });
        }
      }

      if (suggestion) {
        const resp = await startPromptSuggestionTest(suggestion.id, input as StartPromptSuggestionTestInput);
        if (resp.status !== 201) {
          throw new Error((resp.data as { detail?: string }).detail ?? `HTTP ${resp.status}`);
        }
        return { test: resp.data };
      }
      return { run: await startPromptTestRun(promptKey, input) };
    },
    onSuccess: ({ run, test }) => {
      setInspectRunInputs(false);
      if (test && suggestion) {
        setActiveTest(test);
        setActiveRunId(test.draft_run_id);
      } else if (run) {
        setActiveRunId(run.id);
        queryClient.setQueryData(runDetailQueryKey(promptKey, run.id), run);
        queryClient.invalidateQueries({ queryKey: runsQueryKey(promptKey) });
      }
      // Also refetch the profile so mockValueOverrides stop reading as
      // "edited" against the OLD baseline. (isEdited compares override
      // to profile row's rawValue; after refetch they match.)
      queryClient.invalidateQueries({ queryKey: profilesQueryKey(promptKey) });
    },
    // A test whose run failed to start is still saved.
    onSettled: () => {
      if (!suggestion) return;
      queryClient.invalidateQueries({ queryKey: getListPromptSuggestionTestsQueryKey(suggestion.id) });
      // The first test moves the suggestion to testing.
      queryClient.invalidateQueries({ queryKey: getGetPromptSuggestionQueryKey(suggestion.id) });
      queryClient.invalidateQueries({ queryKey: getListPromptSuggestionsQueryKey() });
    },
  });
  // Block the run when the editor has unsaved changes — the server-side
  // run reads `Prompt.draft_instructions` from the DB, so an in-buffer
  // draft that hasn't been saved wouldn't be exercised. Better to force
  // the BCBA to Save first than to run against stale text and confuse
  // them when the result doesn't match what they see in the editor.
  const canRun =
    selectedProfileId !== null && !startRun.isPending && !hasUnsavedDraftEdits && (!suggestion || suggestion.can_test);

  // Clone. Defaults to attaching ``scoped_prompt_key`` so the copy only
  // shows up in THIS prompt's picker (not the global profiles list) —
  // the test-5-7-scenarios flow, where the user clones and we
  // auto-select the copy so the next Run uses it. Passing
  // ``global: true`` (the dialog's "share with all prompts" checkbox)
  // sends ``scoped_prompt_key: null`` instead, so the clone lands
  // universal in one step — previously this needed a second trip
  // through ``publishAsGlobal`` on the resulting copy (PROC-190). No
  // navigation either way: keeps the user on the test bed for their
  // iteration loop. Editing the mock values happens via the existing
  // per-run inline overrides on ProfileVsPromptInputs (persistent
  // edits still go through the profile editor).
  const cloneForThisPrompt = useMutation({
    mutationFn: (args: { id: number; name: string | undefined; global?: boolean }) =>
      cloneLearnerTestProfile(args.id, {
        scoped_prompt_key: resolveCloneScopedKey(promptKey, Boolean(args.global)),
        name: args.name,
      }),
    onSuccess: (clone) => {
      queryClient.invalidateQueries({ queryKey: profilesQueryKey(promptKey) });
      queryClient.invalidateQueries({ queryKey: ["learner-test-profiles"] });
      setSelectedProfileId(clone.id);
      notifySuccess(`Cloned as "${clone.name}" — now selected.`);
    },
    onError: (err) => {
      notifyError(err instanceof Error ? err.message : "Couldn't clone the profile.");
    },
  });

  // Promote-to-global: un-scopes a variant so it shows up in the
  // universal /evaluations/profiles list and every test bed. PATCH
  // with ``scoped_prompt_key: null`` — the backend serializer treats
  // null as "un-scope." Only offered when the picked profile is
  // actually a variant (has a non-null ``scoped_prompt_key``); a
  // profile that's already global has nothing to promote.
  const publishAsGlobal = useMutation({
    mutationFn: (id: number) => updateLearnerTestProfile(id, { scoped_prompt_key: null }),
    onSuccess: (updated) => {
      // Also invalidate the un-namespaced key used by /evaluations/profiles
      // so the global list picks up the newly-universal row on next visit.
      queryClient.invalidateQueries({ queryKey: profilesQueryKey(promptKey) });
      queryClient.invalidateQueries({ queryKey: ["learner-test-profiles"] });
      notifySuccess(`Published "${updated.name}" as global.`);
    },
    onError: (err) => {
      notifyError(err instanceof Error ? err.message : "Couldn't publish the profile.");
    },
  });

  const renameProfile = useMutation({
    mutationFn: (args: { id: number; name: string }) => updateLearnerTestProfile(args.id, { name: args.name }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: profilesQueryKey(promptKey) });
      queryClient.invalidateQueries({ queryKey: ["learner-test-profiles"] });
      notifySuccess(`Renamed to "${updated.name}".`);
    },
    onError: (err) => {
      notifyError(err instanceof Error ? err.message : "Couldn't rename the profile.");
    },
  });

  return (
    <div className="flex flex-col gap-4 p-8 bg-bg-surface border border-border-medium rounded-xl">
      {suggestion ? (
        <div>
          <Heading size="small">Review &amp; test</Heading>
          <Paragraph size="small" colorType="secondary">
            Each run runs the live prompt and this suggestion&apos;s saved draft on the same profile, then a judge
            compares them.
          </Paragraph>
        </div>
      ) : (
        <Heading size="small">Test runs</Heading>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <Label size="small" colorType="secondary" className="uppercase tracking-wide">
            Learner profile
          </Label>
          <div className="flex items-center gap-1.5">
            {selectedProfile && (
              <>
                <button
                  type="button"
                  onClick={() => setActionsDialogOpen(true)}
                  title="Copy, rename, or share this profile"
                  className="cursor-pointer text-sm px-2 py-1 rounded border border-rio-200 bg-rio-50 text-rio-800 hover:bg-rio-100"
                >
                  Profile settings
                </button>
                <Link
                  to={`/evaluations/profiles/${selectedProfile.id}`}
                  title="Add or remove this profile's files and mock data — works for variants too"
                  className="text-sm px-2 py-1 rounded border border-border-medium bg-bg-surface text-text-secondary hover:bg-bg-default hover:text-text-primary"
                >
                  Edit files
                </Link>
              </>
            )}
            <Link
              to="/evaluations/profiles/new"
              className="text-sm px-2 py-1 rounded border border-green-200 bg-green-50 text-green-800 hover:bg-green-100"
            >
              + New profile
            </Link>
            <Link
              to="/evaluations/profiles"
              className="text-sm px-2 py-1 rounded border border-border-medium bg-bg-surface text-text-secondary hover:bg-bg-default hover:text-text-primary"
            >
              Manage profiles
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedProfileId ?? ""}
            onChange={(e) => setSelectedProfileId(e.target.value ? Number(e.target.value) : null)}
            className="flex-1 border border-border-medium rounded-md px-3 py-2 bg-bg-default text-sm text-text-primary"
          >
            <option value="">— Pick a profile —</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.scoped_prompt_key ? " · variant" : p.organization_id ? ` (org ${p.organization_id})` : " (global)"}
              </option>
            ))}
          </select>
          <select
            value={runModel}
            onChange={(e) => setRunModel(e.target.value)}
            aria-label="Model to run with"
            title={`Which model this run uses — flip between current (${availableModels?.default_model ?? "default"}) and a candidate while editing the draft.`}
            className="max-w-44 shrink-0 border border-border-medium rounded-md px-3 py-2 bg-bg-default text-sm text-text-primary"
          >
            <option value="">Current model</option>
            {(availableModels?.models ?? [])
              .filter((m) => !m.is_default)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
          </select>
          <button
            type="button"
            disabled={!canRun}
            onClick={() => startRun.mutate()}
            title="Saves any inline value edits to the profile, then runs the prompt."
            className="shrink-0 text-sm font-medium px-4 py-2 rounded-lg bg-rio-600 text-white hover:bg-rio-700 active:bg-rio-800 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            {startRun.isPending ? "Saving & running…" : "Save & run"}
          </button>
        </div>
        {hasUnsavedDraftEdits && (
          <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Save your draft before running — the run uses the saved draft.</span>
          </div>
        )}
        {suggestion && !suggestion.can_test && (
          <Paragraph size="small" colorType="secondary">
            {needsRedraft(suggestion)
              ? "This suggestion needs re-drafting before it can be tested."
              : "This suggestion\u2019s draft is frozen, so it can\u2019t be tested again."}
          </Paragraph>
        )}
        {profiles.length === 0 && !selectedProfile && (
          <Paragraph size="small" colorType="secondary" className="italic">
            No profiles yet — click &ldquo;+ New profile&rdquo; to create one.
          </Paragraph>
        )}
        {selectedProfile && (
          <ProfileVsPromptInputs
            profile={selectedProfile}
            promptKey={promptKey}
            disabledMockKeys={disabledMockKeys}
            disabledFileUuids={disabledFileUuids}
            mockValueOverrides={mockValueOverrides}
            onToggleMockKey={(key) => setDisabledMockKeys((prev) => toggleMockKey(prev, key))}
            onToggleFile={(uuid) =>
              setDisabledFileUuids((prev) => {
                const next = new Set(prev);
                if (next.has(uuid)) next.delete(uuid);
                else next.add(uuid);
                return next;
              })
            }
            onSetMockValue={(key, value) => setMockValueOverrides((prev) => ({ ...prev, [key]: value }))}
            parsedJsonOverrides={parsedJsonOverrides}
            onOpenParsedEditor={(uuid) => setParsedModalFileUuid(uuid)}
            activeRunSnapshot={
              inspectRunInputs && activeRunDetail
                ? (activeRunDetail.draft_overrides_snapshot as { inputs?: { key: string }[] } | null)
                : null
            }
            activeRunMock={
              inspectRunInputs && activeRunDetail
                ? (activeRunDetail.mock_learner_data_snapshot as Record<string, unknown> | null)
                : null
            }
            activeRunFiles={
              inspectRunInputs && activeRunDetail
                ? (activeRunDetail.test_file_uuids_snapshot as LearnerTestProfileFile[])
                : null
            }
          />
        )}
        {parsedModalFileUuid !== null &&
          (() => {
            const file = selectedProfile?.test_files.find((f) => f.file_uuid === parsedModalFileUuid);
            if (!file) return null;
            return (
              <ParsedJsonModal
                file={file}
                initialOverride={parsedJsonOverrides[file.file_uuid]}
                onSave={(json) => setParsedJsonOverrides((prev) => ({ ...prev, [file.file_uuid]: json }))}
                onClose={() => setParsedModalFileUuid(null)}
              />
            );
          })()}
        {actionsDialogOpen && selectedProfile && (
          <ProfileActionsDialog
            profile={selectedProfile}
            onClose={() => setActionsDialogOpen(false)}
            onRename={(name) => renameProfile.mutate({ id: selectedProfile.id, name })}
            isRenaming={renameProfile.isPending}
            onCloneAsVariant={(name, global) => {
              cloneForThisPrompt.mutate(
                { id: selectedProfile.id, name, global },
                { onSuccess: () => setActionsDialogOpen(false) },
              );
            }}
            isCloning={cloneForThisPrompt.isPending}
            onPublishAsGlobal={() => {
              publishAsGlobal.mutate(selectedProfile.id, {
                onSuccess: () => setActionsDialogOpen(false),
              });
            }}
            isPublishing={publishAsGlobal.isPending}
          />
        )}
      </div>
      {startRun.isError && (
        <div className="text-sm text-red-600">
          <div>Couldn&apos;t start the run.</div>
          <div className="text-xs text-red-500 mt-0.5 break-words">
            {startRun.error instanceof Error ? startRun.error.message : String(startRun.error)}
          </div>
        </div>
      )}

      {shownTest ? (
        <div className="mt-2 flex flex-col gap-4 border-t border-border-subtle pt-4">
          <div className="flex flex-col gap-1 text-sm">
            {shownTest.pairwise_verdict ? (
              <p className="text-text-primary">
                <span className="font-semibold">{TEST_VERDICT_LABELS[shownTest.pairwise_verdict]}.</span>{" "}
                {shownTest.pairwise_summary}
              </p>
            ) : testRunFailed(shownTest) ? (
              <p className="text-text-tertiary">A run failed, so the judge can't compare them.</p>
            ) : testInFlight(shownTest) ? (
              <p className="text-text-tertiary">Running both, then the judge compares them…</p>
            ) : null}
            <span className="text-xs text-text-tertiary">
              Tested {testedFieldLabels(shownTest).join(", ") || "no changes"}
            </span>
          </div>
          {(
            [
              ["live", shownTest.live_run_id],
              ["draft", shownTest.draft_run_id],
            ] as const
          ).map(([side, runId]) =>
            runId != null ? (
              <div key={side} className="flex flex-col gap-2">
                <Label size="small" className="uppercase tracking-wide">
                  {LIVE_AND_DRAFT_LABELS[side]}
                </Label>
                <RunResultView
                  key={runId}
                  promptKey={promptKey}
                  runId={runId}
                  scorable={side === "draft"}
                  onClose={() => {
                    setActiveTest(null);
                    setActiveRunId(null);
                    setInspectRunInputs(false);
                  }}
                />
              </div>
            ) : null,
          )}
        </div>
      ) : (
        activeRunId != null && (
          <div className="mt-2 border-t border-border-subtle pt-4">
            <RunResultView
              // Remounts on run switch. Without it, highlights from the old run
              // stayed over the new output and an in-progress score would save
              // against the next run's review item.
              key={activeRunId}
              promptKey={promptKey}
              runId={activeRunId}
              onClose={() => {
                setActiveRunId(null);
                setInspectRunInputs(false);
              }}
            />
          </div>
        )
      )}

      <div className="flex flex-col gap-2 border-t border-border-subtle pt-4">
        <Label size="small" colorType="secondary" className="uppercase tracking-wide">
          History
        </Label>
        {suggestion ? (
          tests.length === 0 ? (
            <Paragraph size="small" colorType="secondary" className="italic">
              No tests yet.
            </Paragraph>
          ) : (
            <ul className="flex flex-col gap-1 max-h-[24rem] overflow-y-auto pr-1">
              {tests.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTest(t);
                      setActiveRunId(t.draft_run_id);
                      setInspectRunInputs(true);
                    }}
                    className={`w-full text-left flex flex-col gap-0.5 text-sm px-2 py-1 rounded hover:bg-bg-default ${
                      t.id === shownTest?.id ? "bg-bg-selected" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-text-secondary">
                        #{t.id} · {new Date(t.created_date).toLocaleString()}
                      </span>
                      {t.model ? (
                        <span
                          className="whitespace-nowrap rounded bg-rio-100 px-1.5 py-0.5 text-xs font-medium text-rio-800"
                          title={t.model}
                        >
                          {modelLabel(t.model)}
                        </span>
                      ) : null}
                    </div>
                    <span className="text-text-tertiary pl-1">
                      {t.learner_test_profile_name ?? "No profile"} ·{" "}
                      {t.pairwise_verdict
                        ? TEST_VERDICT_LABELS[t.pairwise_verdict]
                        : testRunFailed(t)
                          ? "Run failed"
                          : testInFlight(t)
                            ? "Running"
                            : "No verdict"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : runs.length === 0 ? (
          <Paragraph size="small" colorType="secondary" className="italic">
            No runs yet.
          </Paragraph>
        ) : (
          // Cap the history list so 30+ runs don't push the panel past
          // the viewport — scrolls internally instead.
          <ul className="flex flex-col gap-1 max-h-[24rem] overflow-y-auto pr-1">
            {runs.map((r) => {
              // Look the profile up by id so history rows can show the
              // name instead of just the run id. Profile may have been
              // deleted since the run — fall back to the id in that
              // case so the row still reads sensibly.
              const runProfile = r.learner_test_profile_id
                ? profiles.find((p) => p.id === r.learner_test_profile_id)
                : null;
              const profileLabel = runProfile
                ? runProfile.name
                : r.learner_test_profile_id
                  ? `Profile #${r.learner_test_profile_id} (deleted)`
                  : "No profile";
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveRunId(r.id);
                      setInspectRunInputs(true);
                    }}
                    className={`w-full text-left flex flex-col gap-0.5 text-sm px-2 py-1 rounded hover:bg-bg-default ${
                      r.id === activeRunId ? "bg-bg-selected" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <RunStatusBadge status={r.status} />
                      <span className="text-text-secondary">
                        #{r.id} · {new Date(r.created_date).toLocaleString()}
                      </span>
                      {/* Which model ran it — matters when flipping between
                          current and a candidate on the same draft. */}
                      {r.model ? (
                        <span
                          className="whitespace-nowrap rounded bg-rio-100 px-1.5 py-0.5 text-xs font-medium text-rio-800"
                          title={r.model}
                        >
                          {modelLabel(r.model)}
                        </span>
                      ) : null}
                    </div>
                    <span className={runProfile ? "text-text-tertiary pl-1" : "text-text-tertiary italic pl-1"}>
                      {profileLabel}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export const PromptTestBed: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const [searchParams] = useSearchParams();
  const suggestionId = idParam(searchParams.get(SUGGESTION_PARAM));
  // Editor dirty state lives up here so the runs panel can gate its
  // "Run test" button on unsaved edits — otherwise a BCBA can type a
  // change, hit Run, and see output that doesn't match the visible
  // editor content (the server runs the SAVED draft, not the buffer).
  const [hasUnsavedDraftEdits, setHasUnsavedDraftEdits] = useState(false);

  const {
    data: draft,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: draftQueryKey(key ?? ""),
    queryFn: () => getPromptDraft(key ?? ""),
    // A suggestion has its own draft; the prompt's isn't shown.
    enabled: !!key && suggestionId === null,
  });

  const {
    data: suggestionResp,
    isLoading: suggestionLoading,
    isError: suggestionIsError,
  } = useGetPromptSuggestion(suggestionId ?? 0, {
    query: {
      enabled: suggestionId !== null,
      // Until a re-draft, or a combined one from Review draft, writes back.
      refetchInterval: (query) => {
        const resp = query.state.data;
        return resp?.status === 200 && (resp.data.redraft_in_progress || resp.data.being_incorporated_into)
          ? REDRAFT_POLL_MS
          : false;
      },
    },
  });
  const suggestion = suggestionResp?.status === 200 ? suggestionResp.data : null;

  const {
    data: promptDetailsResp,
    isLoading: detailsLoading,
    isError: detailsIsError,
  } = useGetPromptDetails(key ?? "", { query: { enabled: !!key } });
  const promptDetails = promptDetailsResp?.status === 200 ? promptDetailsResp.data : null;
  const promptName = promptDetails?.name ?? null;
  const promptOrgId = promptDetails?.organization?.id ?? null;

  const errorMessage = useMemo(() => {
    if (isError) {
      return error instanceof Error ? error.message : "Failed to load prompt draft.";
    }
    // Also surface the details fetch failing — otherwise a slow / errored
    // details query leaves the page header with no body once the draft
    // resolves (blank screen, no editor, no spinner).
    if (detailsIsError) return "Failed to load prompt details.";
    if (suggestionId !== null) {
      if (suggestionIsError || (suggestionResp && suggestionResp.status !== 200)) {
        return "Couldn't load this prompt suggestion.";
      }
      if (suggestion && suggestion.prompt_key !== key) return "This suggestion is for a different prompt.";
    }
    if (promptDetailsResp && promptDetailsResp.status !== 200) {
      const detail = (promptDetailsResp.data as { detail?: string } | undefined)?.detail;
      return detail ?? `Failed to load prompt details (status ${promptDetailsResp.status}).`;
    }
    return null;
  }, [
    isError,
    error,
    detailsIsError,
    promptDetailsResp,
    suggestionId,
    suggestionIsError,
    suggestionResp,
    suggestion,
    key,
  ]);

  // When the prompt row has been deleted, redirect to the list. The
  // delay lets Rio's reply poll into the drawer first so the user sees
  // "I've deleted it" before the redirect.
  const navigate = useNavigate();
  const promptWasDeleted = promptDetailsResp?.status === 400;
  useEffect(() => {
    if (!promptWasDeleted) return;
    const t = window.setTimeout(() => navigate("/prompts", { replace: true }), 3000);
    return () => window.clearTimeout(t);
  }, [promptWasDeleted, navigate]);

  if (!key) return null;
  // Wait for BOTH the draft query and the prompt-details query so we
  // don't render the header + an empty body while details is still
  // in-flight. `errorMessage` covers the failed case.
  if (isLoading || detailsLoading || suggestionLoading) return null;

  return (
    <div className="flex flex-col bg-bg-default min-h-full px-4 sm:px-8 lg:px-16 py-8">
      <Breadcrumb
        crumbs={[
          { label: "Prompts", to: "/prompts" },
          { label: key, to: `/prompts/${key}` },
          ...(suggestionId !== null
            ? [{ label: "Evaluations", to: `/prompts/${key}/evaluations` }, { label: "Suggestion" }]
            : [{ label: "Evaluations" }]),
        ]}
      />
      <div className="flex flex-row items-center gap-3 mb-1 flex-wrap">
        <Heading size="medium">Evaluations</Heading>
      </div>
      <div className="flex items-center gap-4 mb-6">
        <span className="text-sm text-text-secondary">
          {promptName && <span className="font-medium text-text-primary">{promptName}</span>}
          {promptName && " "}
          <code className="font-mono">{key}</code>
        </span>
      </div>

      {errorMessage ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 rounded-xl border border-border-medium bg-bg-surface text-center">
          <Heading size="small">Couldn&apos;t load draft</Heading>
          <Paragraph colorType="secondary" className="max-w-lg">
            {errorMessage}
          </Paragraph>
        </div>
      ) : suggestion && promptDetails ? (
        <>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blurple-200 bg-blurple-50 px-5 py-3">
            <div className="flex flex-col">
              <span className="text-xs font-semibold uppercase tracking-wide text-blurple-600">
                Rio&apos;s suggestion for {suggestion.organization_name} · {SUGGESTION_STATUS_LABELS[suggestion.status]}
              </span>
              <span className="text-sm font-medium text-text-primary">{suggestion.theme_title}</span>
              <span className="text-xs text-text-secondary">
                Changes save to this suggestion only — the prompt and its draft stay as they are. Clear a field to drop
                that change.
              </span>
              {needsRedraft(suggestion) ? (
                <span className="mt-1 text-xs font-medium text-text-primary">
                  {redraftNote(suggestion, suggestionDraft(suggestion))}
                  {suggestion.redraft_reason != null &&
                  !suggestion.redraft_in_progress &&
                  suggestion.being_incorporated_into == null
                    ? " Re-draft or dismiss it on Eval Results."
                    : ""}
                </span>
              ) : null}
              {!needsRedraft(suggestion) && suggestion.incorporated.length > 0 ? (
                <span className="mt-1 text-xs text-text-primary">
                  {redraftNote(suggestion, suggestionDraft(suggestion))}
                </span>
              ) : null}
            </div>
            <Link
              to={evalResultsPath(suggestion.organization_id)}
              className="text-sm font-medium text-text-secondary underline"
            >
              Back to Eval Results
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <PromptDraftEditor
              key={`${key}-suggestion-${suggestion.id}`}
              prompt={promptDetails}
              draft={undefined}
              promptKey={key}
              onDirtyChange={setHasUnsavedDraftEdits}
              suggestion={suggestion}
            />
            <RunsPanel
              key={`${key}-suggestion-${suggestion.id}`}
              promptKey={key}
              hasUnsavedDraftEdits={hasUnsavedDraftEdits}
              suggestion={suggestion}
            />
          </div>
        </>
      ) : suggestionId === null && draft && promptDetails ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left column: single form covering every yaml-editable
              field. `key` forces a fresh mount when the route param
              changes so per-prompt local state can't bleed. */}
          <PromptDraftEditor
            key={key}
            prompt={promptDetails}
            draft={draft}
            promptKey={key}
            onDirtyChange={setHasUnsavedDraftEdits}
          />
          {/* Right column: test runs. */}
          <RunsPanel key={key} promptKey={key} hasUnsavedDraftEdits={hasUnsavedDraftEdits} />
        </div>
      ) : null}
      {/* PROC-34: Rio-Prompt chat drawer. Floats above the page; only
          rendered once the prompt key is known so the polling hook can
          key its query correctly. */}
      {/* ``key={key}`` forces a fresh drawer instance when the route
          prompt changes. Without it, the drawer's local state
          (pending, composer draft, greeting-dismissed, lastSeenEditCount)
          persists across navigations, so an in-flight turn on the
          previous prompt can leave ``awaitingReply`` true and block
          sending on the new prompt for up to five minutes. */}
      {key && suggestion ? (
        // The suggestion's own thread: Rio edits its draft, not the prompt's.
        <RioPromptChatDrawer
          key={`${key}-suggestion-${suggestion.id}`}
          promptKey={key}
          promptName={`Suggestion: ${suggestion.theme_title}`}
          promptOrgId={String(suggestion.organization_id)}
          suggestionId={suggestion.id}
          readOnly={!suggestion.can_edit_draft}
        />
      ) : key && suggestionId === null ? (
        <RioPromptChatDrawer key={key} promptKey={key} promptName={promptName ?? key} promptOrgId={promptOrgId} />
      ) : null}
    </div>
  );
};
